import React, { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Badge,
  Chip,
  Stack,
  Divider,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  LocalPharmacy,
  Euro,
  MedicalInformation,
  Save,
  Cancel,
  Spellcheck,
  Category,
  AccountTree,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import TherapiesTab from "./TherapiesTab";
import PricesTab from "./PricesTab";
import DiagnosisGroupsTab from "./DiagnosisGroupsTab";
import TermsTab from "./TermsTab";
import MethodsTab from "./MethodsTab";
import CatalogTab from "./CatalogTab";
import { usePendingChanges } from "./usePendingChanges";
import { useBulkSave } from "../../../stores/MasterTables/therapies.mutations";
import { TherapyType } from "../../../api-client";

const TherapiesManager: React.FC = observer(() => {
  const [activeTab, setActiveTab] = useState(0);
  const [therapyType, setTherapyType] = useState<TherapyType>(TherapyType.Physiotherapie);
  const pending = usePendingChanges();
  const bulkSave = useBulkSave();

  const handleSave = async () => {
    if (!pending.hasChanges) return;
    try {
      await bulkSave.mutateAsync(pending.toBulkPayload());
      pending.clearAll();
    } catch {
      // error handled by mutation
    }
  };

  const handleCancel = () => {
    if (pending.hasChanges && !window.confirm("Alle ungespeicherten Änderungen verwerfen?")) return;
    pending.clearAll();
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "2px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LocalPharmacy sx={{ fontSize: 32, color: "primary.main" }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Heilmittel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Begriffe, Methoden, Heilmittel, Preise und Diagnosegruppen verwalten
            </Typography>
          </Box>
        </Box>

        {/* Pending Changes Actions */}
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={therapyType}
            exclusive
            onChange={(_, val) => { if (val) setTherapyType(val); }}
            size="small"
          >
            <ToggleButton value={TherapyType.Physiotherapie}>Physiotherapie</ToggleButton>
            <ToggleButton value={TherapyType.Ergotherapie}>Ergotherapie</ToggleButton>
          </ToggleButtonGroup>
          {pending.hasChanges && (
            <>
              <Chip
                label={`${pending.changes.length} Änderung${pending.changes.length > 1 ? "en" : ""}`}
                color="warning"
                variant="outlined"
                size="small"
              />
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Cancel />}
                onClick={handleCancel}
                size="small"
              >
                Verwerfen
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={bulkSave.isPending}
                size="small"
              >
                {bulkSave.isPending ? "Speichert…" : "Alle Speichern"}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Pending Changes Summary */}
      {pending.hasChanges && (
        <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {pending.changes.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                color={c.action === "create" ? "success" : c.action === "delete" ? "error" : "primary"}
                variant="outlined"
                onDelete={() => pending.removeChange(c.key)}
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* Sub-Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 500, minHeight: 48 },
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* Ordered along the resolution chain: what the doctor writes, what it
            resolves to, what gets billed — then the supporting tables. */}
        <Tab
          icon={<Spellcheck sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Begriffe"
        />
        <Tab
          icon={<Category sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Methoden"
        />
        <Tab
          icon={<LocalPharmacy sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Heilmittel Positionen"
        />
        <Tab
          icon={<Euro sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label={
            <Badge
              badgeContent={pending.changes.filter((c) => c.entityType === "price").length || undefined}
              color="warning"
              sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem" } }}
            >
              Preise
            </Badge>
          }
        />
        <Tab
          icon={<MedicalInformation sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Diagnosegruppen"
        />
        <Tab
          icon={<AccountTree sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Katalog"
        />
      </Tabs>

      <Divider sx={{ mb: 2 }} />

      {/* Tab Content */}
      {/* The MD-232 levels save immediately rather than joining the staged
          "Alle Speichern" batch: a term edit replaces its alias and resolution
          lists wholesale and the server validates them together, so deferring it
          would mean showing a change the server might still reject. */}
      {activeTab === 0 && <TermsTab therapyType={therapyType} />}
      {activeTab === 1 && <MethodsTab therapyType={therapyType} />}
      {activeTab === 2 && <TherapiesTab pending={pending} therapyType={therapyType} />}
      {activeTab === 3 && <PricesTab pending={pending} therapyType={therapyType} />}
      {activeTab === 4 && <DiagnosisGroupsTab pending={pending} therapyType={therapyType} />}
      {activeTab === 5 && <CatalogTab therapyType={therapyType} />}
    </Box>
  );
});

export default TherapiesManager;
